'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useAuth } from '@/hooks/use-auth'
import { ROLES } from '@/lib/constants'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Header } from '@/components/layout/header'
import { Footer } from '@/components/layout/footer'
import { AlertCircle, ArrowLeft, Clock, Lock } from 'lucide-react'
import Link from 'next/link'

interface ArticleAccessGuardProps {
  article: {
    id: string
    title: string
    isPublished: boolean
    publishedAt: string | null
  }
  children: React.ReactNode
}

export function ArticleAccessGuard({ article, children }: ArticleAccessGuardProps) {
  const { user, hasRole, loading } = useAuth()
  const router = useRouter()
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    // Since the API now only returns published articles to this page,
    // if we have an article, it should be viewable
    // Only restrict access if article is explicitly unpublished or scheduled for future
    const isPublishedAndAvailable = article.isPublished && 
      article.publishedAt && 
      new Date(article.publishedAt) <= new Date()
    
    if (isPublishedAndAvailable) {
      setShouldShow(true)
      return
    }
    
    // For unpublished or future-scheduled articles, check admin access
    // Don't wait for loading to complete - if we're not loading and have no user, assume no access
    if (loading) {
      setShouldShow(false)
      return
    }
    
    const isAdmin = user && (hasRole(ROLES.SITE_ADMIN) || hasRole(ROLES.NEWS_EDITOR))
    setShouldShow(!!isAdmin)
  }, [article, user, hasRole, loading])

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="h-10 w-32 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="h-6 w-32 bg-muted rounded-full animate-pulse"></div>
                  <div className="space-y-2">
                    <div className="h-10 bg-muted rounded animate-pulse"></div>
                    <div className="h-10 w-3/4 bg-muted rounded animate-pulse"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-6 bg-muted rounded animate-pulse"></div>
                    <div className="h-6 w-5/6 bg-muted rounded animate-pulse"></div>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <div className="h-5 w-24 bg-muted rounded animate-pulse"></div>
                    <div className="h-5 w-32 bg-muted rounded animate-pulse"></div>
                    <div className="h-5 w-20 bg-muted rounded animate-pulse"></div>
                  </div>
                </div>
                <div className="space-y-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-4 bg-muted rounded animate-pulse"></div>
                      <div className="h-4 bg-muted rounded animate-pulse"></div>
                      <div className="h-4 w-4/5 bg-muted rounded animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="space-y-6">
                <div className="h-64 bg-muted rounded-lg animate-pulse"></div>
                <div className="h-80 bg-muted rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (!shouldShow) {
    const isUnpublished = !article.isPublished
    const isScheduled = article.publishedAt && new Date(article.publishedAt) > new Date()
    
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              {isUnpublished ? (
                <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
              ) : isScheduled ? (
                <Clock className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
              ) : (
                <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
              )}
              <h1 className="text-2xl font-bold mb-2">
                {isUnpublished ? 'Article Not Published' : isScheduled ? 'Article Not Yet Available' : 'Article Not Found'}
              </h1>
              <p className="text-muted-foreground mb-6">
                {isUnpublished 
                  ? 'This article is not yet published and requires admin access to view.'
                  : isScheduled 
                  ? `This article is scheduled to be published on ${new Date(article.publishedAt!).toLocaleDateString()}.`
                  : 'The article you are looking for does not exist or is not available.'
                }
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full">
                  <Link href="/news">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to News
                  </Link>
                </Button>
                {isUnpublished && (
                  <Button variant="outline" asChild className="w-full">
                    <Link href="/auth/login">
                      Log In as Admin
                    </Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
        
        <Footer />
      </div>
    )
  }

  return <>{children}</>
}